import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Building,
  IdCard,
  Info,
  Lock,
  MapPin,
  Search,
  Settings,
} from "lucide-react";

import { Label } from "@radix-ui/react-label";
import CloseIcon from "@mui/icons-material/Close";

import { cn } from "../../../lib/utils";
import { getAPIPayload } from "../../../apis/getApi";
import FindProperty from "./FindProprty";
import { postAPIPayload } from "../../../apis/postApi";
import AlertDialog from "../../common/AlertDialog";
import { Loader } from "../../common/loader";
import EpAwareIdVerification from "../../user-signup/SignupTaiwindComponents/EpAwareIdVerification";
import { isCarInEscrowEnrollment } from "../../for-service-provider/carInEscrowSignup";
import {
  buildEpAwareApplyPropertyPayload,
  decryptPayload,
  formatEpAwareMailingAddressLabel,
  getObjLength,
  persistSignupVerificationMethod,
  propertyRiskReportDataObj,
} from "../../helpers/utils";
import {
  buildComplimentaryEnhancedMonitoringPayload,
  isAdminEldoradoComplimentaryEnhancedSelected,
  isLoggedInSuperOrInternalAdmin,
} from "../../helpers/eldoradoSignup";
import { resolveProfileUsCellPhone } from "../../helpers/usCellPhone";
import {
  canOfferEpAwareIdVerification,
  shouldAllowEpAwareVerification,
} from "../../helpers/epAwareVerificationGuard";
import FindPropertyByParcel from "./FindPropertyByParcel";
import AddPropertyMailingMatches, {
  type MailingMatchesResponse,
} from "./AddPropertyMailingMatches";
import AddPropertyMailingSearchProgress from "./AddPropertyMailingSearchProgress";
import { Button } from "../../common/customDesignedComponents/button";
import { FormInput } from "../../common/customDesignedComponents/FormInput";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import ProeprtyListV2 from "../../user-signup/SignupTaiwindComponents/ProeprtyListV2";
import PropertyInfoBox from "../../user-signup/SignupTaiwindComponents/PropertyInfoBox";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../common/customDesignedComponents/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../common/customDesignedComponents/tabs";

const FindPropertyComponent = ({
  handleBack = () => {}, //NA
  onBtnClick,
  propertyValues,
  setPropertyValues,
  validate,
  handleToggle,
  handleClose,
  questionsErrMsg,
  open,
  errMsg,
  setErrMsg,
  handleChange = (value: string) => {},
  handleSkip, //NA
  searchedAddress,
  setSearchedAddress,
  //
  isAdminCreateUser = false,
  isByPassOptionAvailable = false,
  onChangeModalScreen = (screen: string = "questions") => {},
  onCloseModalHandler = (_result?: string) => {},
  editPropertiesDetails = {},
  selectedData = { address: "" },
  editProperty = false,
  isResumeSignup = false,
  selectedRowData = {},
  isAssistant = false,
  backToCategoryWisePricingScreen = () => {},
  isAdmin = false,
  isUpgradeCategory = false,
  userEmail = undefined,
  isTailwindComponent = false,
  handleCloseAddPropertyModal = () => {},
  setQuestionsErrMsg = (value: string) => {},
  isCustomer = false,
  isServiceProvider = false,
  agreementCategory = undefined,
}) => {
  type AddPropertyEntryStep = "mailing_matches" | "address_search";
  type ServicerEntryMethod = "choose" | "manual" | "id" | "id_address_fallback";

  const [entryStep, setEntryStep] =
    useState<AddPropertyEntryStep>("address_search");
  const [showNoMailingMatchesBanner, setShowNoMailingMatchesBanner] =
    useState(false);
  const [mailingSearchLoading, setMailingSearchLoading] = useState(
    Boolean(isCustomer && isTailwindComponent),
  );
  const [mailingMatchesData, setMailingMatchesData] =
    useState<MailingMatchesResponse | null>(null);
  const [activeTab, setActiveTab] = useState("address");
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showUnitInput, setShowUnitInput] = useState(false);
  const [showPropertyListing, setShowPropertyListing] = useState(false);
  const [proeprtySearchErrorByUnit, setProeprtySearchErrorByUnit] =
    useState(undefined);
  const [modalUnitNumber, setModalUnitNumber] = useState("");
  const [isPropertyConfirmed, setIsPropertyConfirmed] = useState<
    boolean | null
  >(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null,
  );
  // CAR clients in escrow are never ID verified: go straight to the search.
  const carInEscrowEntry = Boolean(isServiceProvider) && isCarInEscrowEnrollment();
  const [entryMethod, setEntryMethod] = useState<ServicerEntryMethod>(
    isServiceProvider && !carInEscrowEntry ? "choose" : "manual",
  );
  const [epAwareSessionId, setEpAwareSessionId] = useState<string | null>(null);
  const [idMailingOnFile, setIdMailingOnFile] = useState<{
    address_line?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null>(null);
  const [epAwareApplyError, setEpAwareApplyError] = useState("");
  const [epAwareApplying, setEpAwareApplying] = useState(false);
  const [complimentaryEnrollResult, setComplimentaryEnrollResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const pendingComplimentaryEnhancedRef = useRef(false);

  const {
    getPropertyData,
    getSearchedPropertyData,
    userPropertyData,
    propertyQuestion,
    propertyLoader,
    showSecurityInfoScreen,
    selectedProperty,
    isQuestionByPass,
  } = useSelector((state: any) => state.properties);

  const { paymentData, paymnetLoader } = useSelector(
    (state: any) => state.signUpUser,
  );

  const { data: userProfileData } = useSelector(
    (state: any) => state?.userProfile?.userProfileDetails,
  );
  const { servicerProfessionalDetails } = useSelector(
    (state: any) => state?.servicerProfile,
  );
  const selectedUserID = useSelector(
    (state: any) => state?.properties?.selectedUserID,
  );

  const dispatch = useDispatch();
  const resolvedCategory =
    agreementCategory ||
    decryptPayload(localStorage.getItem("category")) ||
    "complete_protection";
  // Question bypass skips owner Q&A after property select. For SP, government ID
  // is still a valid find-property method and must remain available.
  const treatAsQuestionBypassForEpAware =
    Boolean(isQuestionByPass) && !isServiceProvider;
  const blockEpAwareForBypass =
    treatAsQuestionBypassForEpAware &&
    resolvedCategory === "complete_protection";
  const epAwareEntryAllowed =
    !carInEscrowEntry &&
    canOfferEpAwareIdVerification({
      category: resolvedCategory,
      isQuestionByPass: treatAsQuestionBypassForEpAware,
    });
  const epAwareActiveAllowed = shouldAllowEpAwareVerification({
    category: resolvedCategory,
    isQuestionByPass: treatAsQuestionBypassForEpAware,
    verificationMethod: "id",
    isExplicitIdChoice: true,
  });
  const clientUserId =
    (selectedRowData as { user_id?: number | string } | null | undefined)
      ?.user_id || selectedUserID;
  const resolvedUserId =
    (isAdminCreateUser ? clientUserId : null) ||
    userProfileData?.user_id ||
    servicerProfessionalDetails?.data?.user_id;

  const epAwareStartContext = useMemo(
    () => ({
      userId: resolvedUserId,
      propertyAddress: {},
      partyEmail: userProfileData?.email,
      partyPhone: resolveProfileUsCellPhone(userProfileData) || undefined,
      partyName: userProfileData?.first_name
        ? `${userProfileData.first_name} ${userProfileData.last_name || ""}`.trim()
        : undefined,
    }),
    [resolvedUserId, userProfileData],
  );

  const buildIdVerifyPayload = useCallback(
    () => ({
      user_id: resolvedUserId,
      answers: { ep_aware: "verified" },
      step: 2,
      step_key: "VERIFY_VIA_EPAWARE",
    }),
    [resolvedUserId],
  );

  useEffect(() => {
    if (!blockEpAwareForBypass) {
      return;
    }

    if (entryMethod === "id" || entryMethod === "id_address_fallback") {
      setEntryMethod(isServiceProvider ? "choose" : "manual");
    }
  }, [blockEpAwareForBypass, entryMethod, isServiceProvider]);

  const advanceAfterIdVerification = useCallback(
    (response?: unknown) => {
      if (response) {
        dispatch(propertiesAction.setVerifedAnswersData(response));
      }
      localStorage.setItem("isOwnerVerified", "1");
      dispatch(userSigupAction.showSecurityInfoScreen(true));
      dispatch(propertiesAction.showSecurityInfoScreen(true));
      onChangeModalScreen();
    },
    [dispatch, onChangeModalScreen],
  );

  const handleIdSearchDifferentAddress = useCallback(
    (
      sessionId: string,
      idMailingAddress: {
        address_line?: string;
        city?: string;
        state?: string;
        zip?: string;
      } | null,
    ) => {
      setEpAwareSessionId(sessionId);
      setIdMailingOnFile(idMailingAddress);
      setEpAwareApplyError("");
      setEntryMethod("id_address_fallback");
      setIsPropertyConfirmed(null);
      setPropertyValues({});
      setSearchedAddress("");
      dispatch(propertiesAction.addNewProperty({}));
    },
    [dispatch, setPropertyValues, setSearchedAddress],
  );

  const applyEpAwareFallbackProperty = useCallback(async () => {
    if (blockEpAwareForBypass) {
      return false;
    }

    if (!epAwareSessionId) {
      setEpAwareApplyError(
        "Your ID verification session expired. Go back and verify your ID again.",
      );
      return false;
    }

    const applyProperty = buildEpAwareApplyPropertyPayload(selectedProperty);
    if (!applyProperty) {
      setEpAwareApplyError(
        "We could not read the selected property details. Search again and retry.",
      );
      return false;
    }

    setEpAwareApplying(true);
    setEpAwareApplyError("");

    try {
      const response = await postAPIPayload({
        url: `properties/ep-aware-verification/${epAwareSessionId}/apply-properties`,
        data: {
          ...buildIdVerifyPayload(),
          require_mailing_address_match: true,
          properties: [applyProperty],
        },
        token: true,
      });

      if (response?.success) {
        advanceAfterIdVerification(response);
        return true;
      }

      const idMailing = response?.data?.id_mailing_address;
      const propertyMailing = response?.data?.property_mailing_address;
      let message =
        response?.message ||
        "The mailing address on this property does not match your government ID.";

      if (idMailing?.address_line && propertyMailing?.address_line) {
        message = `${message} ID on file: ${formatEpAwareMailingAddressLabel(
          idMailing,
        )}. Property mailing address: ${formatEpAwareMailingAddressLabel(
          propertyMailing,
        )}.`;
      }

      setEpAwareApplyError(message);
      return false;
    } catch (applyError: any) {
      setEpAwareApplyError(
        applyError?.message ||
          "Unable to verify this property against your government ID.",
      );
      return false;
    } finally {
      setEpAwareApplying(false);
    }
  }, [
    advanceAfterIdVerification,
    blockEpAwareForBypass,
    buildIdVerifyPayload,
    epAwareSessionId,
    selectedProperty,
  ]);

  const renderPropertyEntryPicker = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-xl md:text-2xl font-display font-medium text-foreground mb-3">
          How would you like to find your property?
        </h1>
        <p className="text-muted-foreground text-sm">
          Verify with your government ID, or search by address or parcel.
        </p>
      </div>

      <div className="grid gap-4">
        {epAwareEntryAllowed ? (
          <button
            type="button"
            onClick={() => {
              persistSignupVerificationMethod("id");
              setEntryMethod("id");
            }}
            className="text-left rounded-2xl border border-border p-5 hover:border-secondary hover:shadow-md transition-all bg-background"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                <IdCard className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">
                  Upload / verify with government ID
                </h3>
                <p className="text-sm text-muted-foreground">
                  Scan a QR code on your phone, complete EpAware ID
                  verification, then choose properties whose tax-record mailing
                  address matches your ID.
                </p>
              </div>
            </div>
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => {
            persistSignupVerificationMethod("manual");
            setEntryMethod("manual");
          }}
          className="text-left rounded-2xl border border-border p-5 hover:border-secondary hover:shadow-md transition-all bg-background"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
              <Search className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-1">
                Search manually
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter the property address or parcel number, then confirm the
                listing before continuing.
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  const handleShowAddressSearch = useCallback(
    (showNoMatchesBanner: boolean) => {
      setShowNoMailingMatchesBanner(showNoMatchesBanner);
      if (showNoMatchesBanner) {
        sessionStorage.setItem("addPropertyMailingNoMatch", "1");
      }
      setEntryStep("address_search");
    },
    [],
  );

  useEffect(() => {
    if (entryStep === "mailing_matches" && !resolvedUserId) {
      setEntryStep("address_search");
    }
  }, [entryStep, resolvedUserId]);

  useEffect(() => {
    if (!isCustomer || !isTailwindComponent || !resolvedUserId) {
      setMailingSearchLoading(false);
      return;
    }

    let cancelled = false;

    const loadMailingMatches = async () => {
      setMailingSearchLoading(true);

      try {
        const response = await getAPIPayload({
          url: "properties/ep-aware-verification/add-property-mailing-matches",
          token: true,
        });

        if (cancelled) {
          return;
        }

        if (!response?.success) {
          setEntryStep("address_search");
          return;
        }

        const data = response.data as MailingMatchesResponse;
        setMailingMatchesData(data);

        if (data.eligible && (data.candidates?.length ?? 0) > 0) {
          setEntryStep("mailing_matches");
          return;
        }

        setShowNoMailingMatchesBanner(Boolean(data.eligible));
        if (data.eligible) {
          sessionStorage.setItem("addPropertyMailingNoMatch", "1");
        }
        setEntryStep("address_search");
      } catch {
        if (!cancelled) {
          setEntryStep("address_search");
        }
      } finally {
        if (!cancelled) {
          setMailingSearchLoading(false);
        }
      }
    };

    loadMailingMatches();

    return () => {
      cancelled = true;
    };
  }, [isCustomer, isTailwindComponent, resolvedUserId]);

  const unitModalActionsDisabled = propertyLoader;
  const propertySearchTabsRef = useRef<HTMLDivElement>(null);
  const propertyListRef = useRef<HTMLDivElement>(null);
  const shouldScrollToPropertyListRef = useRef(false);

  const scrollToPropertySearchTabs = () => {
    requestAnimationFrame(() => {
      propertySearchTabsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handlePropertyConfirmYes = () => {
    setIsPropertyConfirmed(true);
    localStorage.removeItem("searched_Add");
  };

  const handlePropertyConfirmNo = () => {
    handleBack();
    handleSearchAgain();
  };

  const handleSearchAgain = () => {
    setIsPropertyConfirmed(null);
    setPropertyValues({});
    setSearchedAddress("");
    setQuestionsErrMsg("");
    // Clear both property stores so add-property-in-report error state
    // cannot re-trigger create/questions on the next select.
    dispatch(propertiesAction.addNewProperty({}));
    dispatch(propertiesAction.setGeneratedQuestionsData({}));
    dispatch(propertiesAction.generateQuestionsInputParams({}));
    dispatch(userSigupAction.addNewProperty({}));
    dispatch(userSigupAction.clearPropertyQuestion());
  };

  const handleNextStep = async () => {
    if (isServiceProvider && entryMethod === "id_address_fallback") {
      await applyEpAwareFallbackProperty();
      return;
    }

    const goToPlansAfterPropertyAdded =
      isCustomer &&
      !isUpgradeCategory &&
      !isAdminCreateUser &&
      !isResumeSignup;

    // Admin Add Property Complimentary: enroll from PropertyInfoBox Next.
    if (
      (isAdmin || isAdminCreateUser) &&
      resolvedCategory === "enhanced_monitoring" &&
      isAdminEldoradoComplimentaryEnhancedSelected() &&
      !isResumeSignup
    ) {
      const payload = buildComplimentaryEnhancedMonitoringPayload(
        propertyQuestion,
        (selectedRowData as { user_id?: number | string } | null | undefined)
          ?.user_id ||
          selectedUserID ||
          clientUserId,
        {
          isAdminComplimentary: isLoggedInSuperOrInternalAdmin(userProfileData),
        },
      );
      if (!payload) {
        setComplimentaryEnrollResult({
          success: false,
          message:
            "Unable to activate Complimentary Enhanced Monitoring for this property. Please try again.",
        });
        return;
      }
      setComplimentaryEnrollResult(null);
      dispatch(userSigupAction.fetchPaymentData({}));
      pendingComplimentaryEnhancedRef.current = true;
      dispatch(
        userSigupAction.getPayment({
          url: "payment-enhanced-monitoring",
          data: payload,
          token: true,
        }),
      );
      return;
    }

    if (getObjLength(propertyQuestion?.data)) {
      const property_search_data = propertyRiskReportDataObj(
        propertyQuestion?.data,
      );
      const internal_property_details = JSON.parse(
        propertyQuestion?.data?.ttb_details || "{}",
      );
      localStorage.setItem(
        "property_search_data",
        JSON.stringify({
          ...property_search_data,
          ...internal_property_details,
          risk_report_id:
            propertyQuestion?.data?.risk_report_id ??
            propertyQuestion?.data?.report_id ??
            propertyQuestion?.data?.id,
        }),
      );
    }

    if (goToPlansAfterPropertyAdded) {
      onBtnClick();
      onChangeModalScreen("categoryWisePricing");
      return;
    }

    // Basic / Enhanced have no ownership questions: admin resume signup goes
    // back to Owner Verified instead of an empty Security Questions step.
    const skipQuestionsForMonitoringResume =
      isResumeSignup && resolvedCategory !== "complete_protection";

    if (!isQuestionByPass && !skipQuestionsForMonitoringResume) {
      dispatch(userSigupAction.showSecurityInfoScreen(true));
      dispatch(propertiesAction.showSecurityInfoScreen(true));
    }
    onBtnClick();
    console.log(isAdmin, "QQQ 5 isQuestionByPass==>", isQuestionByPass);

    if (skipQuestionsForMonitoringResume) {
      onChangeModalScreen("propertyList");
      return;
    }

    if (isQuestionByPass) {
      // Add Client / Admin create-user / admin resume signup must land on Owner
      // Verification so they can add another property or press Next for plans.
      // Skipping straight to subscriptions drops that step after "Add Another".
      onChangeModalScreen(
        isResumeSignup || isAdminCreateUser
          ? "propertyList"
          : isAdmin || isByPassOptionAvailable
            ? "subscriptions"
            : "propertyList",
      );
      return;
    }
    localStorage.setItem("button", "back");
  };

  const handleUnitModalYes = () => {
    setShowUnitInput(true);
  };

  const handleUnitModalNo = () => {
    // dispatch(userSigupAction.fetchUserPropertyData());
    setShowUnitModal(false);
    setShowPropertyListing(true);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      shouldScrollToPropertyListRef.current = true;
    }
  };

  const handleUnitModalClose = (open: boolean) => {
    setShowUnitModal(open);
    if (!open) {
      setShowUnitInput(false);
      setModalUnitNumber("");
      setProeprtySearchErrorByUnit(undefined);
      // dispatch(userSigupAction.fetchUserPropertyData());
    }
  };

  const handleUnitModalSubmit = () => {
    let data = {
      ...userPropertyData?.data,
      unit_no: modalUnitNumber,
    };
    console.log("data==>", data);
    dispatch(
      propertiesAction.fetchUserProperty({
        url: "properties",
        data: data,
        token: false,
      }),
    );
    setPropertyValues(data);
  };

  useEffect(() => {
    if (getObjLength(getPropertyData)) {
      if (getPropertyData?.success) {
        if (getPropertyData?.data?.length > 5) {
          if (!selectedPropertyId && !getObjLength(propertyQuestion)) {
            setShowUnitModal(true);
          }

          // if (getObjLength(propertyQuestion)) {
          //   setShowUnitModal(false);
          // }
        } else {
          if (getObjLength(propertyQuestion) && propertyQuestion?.success) {
            setShowUnitModal(false);
            setShowUnitInput(false);
            setProeprtySearchErrorByUnit(undefined);
            // setModalUnitNumber("");
            setQuestionsErrMsg("");
            setShowPropertyListing(false);
            return;
          } else {
            // dispatch(userSigupAction.fetchUserPropertyData());
            setProeprtySearchErrorByUnit(propertyQuestion?.data?.message);
            setModalUnitNumber("");
            setShowPropertyListing(false);
          }
        }
      } else {
        setShowPropertyListing(false);
        setProeprtySearchErrorByUnit(
          getPropertyData?.data?.message ||
            getPropertyData?.message ||
            "No property found for that search.",
        );
        handleClose?.();
      }
      const addPropertyFailed =
        getObjLength(propertyQuestion) && !propertyQuestion?.success;
      if (
        getPropertyData?.data?.length <= 5 &&
        getPropertyData?.data?.length > 1 &&
        // Keep the selected property + conflict message visible instead of
        // bouncing back to the list with no obvious error.
        !(addPropertyFailed && getObjLength(selectedProperty))
      ) {
        setShowPropertyListing(true);
      }
      if (getPropertyData?.data?.length === 1 && addPropertyFailed) {
        setShowPropertyListing(false);
      }
      if (getObjLength(propertyQuestion) && propertyQuestion?.success) {
        setShowPropertyListing(false);
        setSelectedPropertyId(null);
        setQuestionsErrMsg("");
      }
    }
  }, [getPropertyData, propertyQuestion, selectedProperty]);

  useEffect(() => {
    console.log("QQQ 4 showSecurityInfoScreen==>", showSecurityInfoScreen);
    if (showSecurityInfoScreen) {
      const goToPlansAfterPropertyAdded =
        isCustomer &&
        !isUpgradeCategory &&
        !isAdminCreateUser &&
        !isResumeSignup;
      onChangeModalScreen(
        goToPlansAfterPropertyAdded ? "categoryWisePricing" : "questions",
      );
      dispatch(propertiesAction.showSecurityInfoScreen(false));
    }
  }, [
    showSecurityInfoScreen,
    isCustomer,
    isUpgradeCategory,
    isAdminCreateUser,
    isResumeSignup,
    onChangeModalScreen,
    dispatch,
  ]);

  useEffect(() => {
    if (!pendingComplimentaryEnhancedRef.current) {
      return;
    }
    if (paymnetLoader) {
      return;
    }
    if (!getObjLength(paymentData)) {
      return;
    }

    pendingComplimentaryEnhancedRef.current = false;
    if (paymentData?.success) {
      setComplimentaryEnrollResult({
        success: true,
        message:
          paymentData?.message ||
          paymentData?.data?.message ||
          "Complimentary Enhanced Monitoring activated for this property.",
      });
      return;
    }

    setComplimentaryEnrollResult({
      success: false,
      message:
        paymentData?.message ||
        paymentData?.data?.message ||
        "Unable to activate Complimentary Enhanced Monitoring.",
    });
  }, [paymentData, paymnetLoader]);

  useEffect(() => {
    if (
      !shouldScrollToPropertyListRef.current ||
      !showPropertyListing ||
      !propertyListRef.current
    ) {
      return;
    }

    shouldScrollToPropertyListRef.current = false;
    requestAnimationFrame(() => {
      propertyListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [showPropertyListing]);

  const hasAddPropertyError = Boolean(questionsErrMsg?.length);
  const hasSelectedProperty = getObjLength(selectedProperty);
  const hasSearched =
    !showUnitModal &&
    ((getObjLength(getSearchedPropertyData) &&
      getSearchedPropertyData?.success) ||
      showPropertyListing ||
      (hasAddPropertyError && hasSelectedProperty))
      ? true
      : false;
  const showPropertyConfirmation =
    hasSearched &&
    !showPropertyListing &&
    (Boolean(getSearchedPropertyData?.success) ||
      (hasAddPropertyError && hasSelectedProperty));
  const showEntryPicker =
    isServiceProvider && entryMethod === "choose" && !hasSearched;
  const showIdVerification =
    isServiceProvider && entryMethod === "id" && epAwareActiveAllowed;
  const showManualSearch =
    !isServiceProvider ||
    entryMethod === "manual" ||
    entryMethod === "id_address_fallback";
  const idMailingLabel = formatEpAwareMailingAddressLabel(idMailingOnFile);

  if (entryStep === "mailing_matches" && resolvedUserId && mailingMatchesData) {
    return (
      <AddPropertyMailingMatches
        userId={resolvedUserId}
        matches={mailingMatchesData}
        onSearchByAddress={handleShowAddressSearch}
      />
    );
  }

  return (
    <>
      <Dialog open={mailingSearchLoading} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          hideCloseButton
          preventDismiss
          containerClassName="z-[9999]"
        >
          <DialogTitle className="sr-only">
            Searching for properties that match your mailing address
          </DialogTitle>
          <AddPropertyMailingSearchProgress isLoading variant="modal" />
        </DialogContent>
      </Dialog>

      <div
        className={`min-h-screen pb-16 ${showPropertyListing ? "add-property-list-visible" : ""}`}
      >
        <div className="container mx-auto">
          {/* Main Content - Two Column Layout */}
          <div
            className={`max-w-5xl mx-auto ${
              !hasSearched || showPropertyConfirmation ? "max-w-2xl" : ""
            }`}
          >
            <div
              className={`grid gap-6 ${
                showPropertyListing ? "lg:grid-cols-2" : "lg:grid-cols-1"
              }`}
            >
              {/* Left Column - Property Info Box OR Property List */}
              {hasSearched ? (
                showPropertyListing ? (
                  <div ref={propertyListRef}>
                    <ProeprtyListV2
                      onTimeout={() => {
                        dispatch(propertiesAction.addNewProperty({}));
                        setShowPropertyListing(false);
                      }}
                      selectedPropertyId={selectedPropertyId}
                      setSelectedPropertyId={setSelectedPropertyId}
                      getPropertyData={getPropertyData}
                      getSearchedPropertyData={(data: any) => {
                        setQuestionsErrMsg("");
                        dispatch(
                          propertiesAction.selectedUserPropertyData(data),
                        );
                      }}
                    />
                  </div>
                ) : (
                  <PropertyInfoBox
                    handlePropertyConfirmYes={handlePropertyConfirmYes}
                    handlePropertyConfirmNo={handlePropertyConfirmNo}
                    isPropertyConfirmed={isPropertyConfirmed}
                    onNextStep={handleNextStep}
                    onSearchAgain={handleSearchAgain}
                    category={resolvedCategory}
                    errorMessage={questionsErrMsg}
                  />
                )
              ) : null}

              {/* Right Column - Form (hidden during property confirmation) */}
              {!showPropertyConfirmation && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-card rounded-2xl border border-border shadow-lg overflow-visible"
                >
                  {/* Secure Connection Bar */}
                  <div className="relative rounded-t-2xl bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                    <Lock className="h-4 w-4 text-white" />
                    <span className="text-white font-medium text-sm">
                      Secure connection
                    </span>
                    <button
                      type="button"
                      onClick={handleCloseAddPropertyModal}
                      aria-label="Close add property modal"
                      className="add-proeprty-modal-close-btn modal-close-btn close-text hide-inner-close absolute right-3 top-1/2 -translate-y-1/2 bg-transparent p-1"
                    >
                      <CloseIcon
                        className="hide-inner-close"
                        sx={{ fontSize: 26, color: "white !important" }}
                      />
                    </button>
                  </div>

                  {/* Form Content */}
                  <div className="overflow-visible p-6 md:p-8">
                    {showEntryPicker && renderPropertyEntryPicker()}

                    {showIdVerification && (
                      <EpAwareIdVerification
                        onBack={() => setEntryMethod("choose")}
                        buildVerifyPayload={buildIdVerifyPayload}
                        startContext={epAwareStartContext}
                        useAuthToken
                        onPropertyProtected={() => advanceAfterIdVerification()}
                        onApplySuccess={(response) =>
                          advanceAfterIdVerification(response)
                        }
                        onSearchDifferentAddress={
                          handleIdSearchDifferentAddress
                        }
                        autoStart={epAwareActiveAllowed}
                      />
                    )}

                    {showManualSearch && (
                      <>
                        {showNoMailingMatchesBanner ? (
                          <div className="mb-6 rounded-xl border border-secondary/20 bg-secondary/5 p-4 text-sm text-foreground flex gap-3">
                            <Info className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                            <p>
                              We searched the county for any additional
                              properties that match your mailing address on file
                              and couldn&apos;t find one. Search below to add a
                              property by address or parcel.
                            </p>
                          </div>
                        ) : null}
                        {entryMethod === "id_address_fallback" && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 mb-4">
                            <p className="font-medium mb-1">
                              Search for your property
                            </p>
                            <p>
                              Your ID is verified. Search for the property you
                              want to protect. The county tax-record mailing
                              address must match your government ID
                              {idMailingLabel ? ` (${idMailingLabel})` : ""}.
                            </p>
                          </div>
                        )}
                        {epAwareApplyError ? (
                          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-4">
                            {epAwareApplyError}
                          </div>
                        ) : null}
                        {isServiceProvider &&
                          !carInEscrowEntry &&
                          !hasSearched &&
                          entryMethod !== "choose" && (
                            <button
                              type="button"
                              onClick={() => {
                                if (entryMethod === "id_address_fallback") {
                                  setEntryMethod("id");
                                  setEpAwareSessionId(null);
                                  setIdMailingOnFile(null);
                                  setEpAwareApplyError("");
                                } else {
                                  setEntryMethod("choose");
                                }
                                handleSearchAgain();
                              }}
                              className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors mb-4"
                            >
                              {entryMethod === "id_address_fallback"
                                ? "Back to ID verification"
                                : "Change how you find your property"}
                            </button>
                          )}
                        <h1 className="text-xl font-display font-medium text-foreground text-center mb-2">
                          Property Search
                        </h1>
                        <p className="text-muted-foreground text-center text-sm mb-6">
                          Please provide us with the address of the property you
                          would like to have monitored and protected.
                        </p>

                        {/* Tabs */}
                        <div ref={propertySearchTabsRef}>
                          <Tabs
                            value={activeTab}
                            onValueChange={(value) => {
                              setActiveTab(value);
                              if (!hasSearched) {
                                handleChange(value);
                              }
                            }}
                            className="mb-4"
                          >
                            <TabsList className="grid h-auto w-full grid-cols-2 gap-0 border-b border-border bg-transparent p-0 shadow-none">
                              <TabsTrigger
                                value="address"
                                className="flex items-center justify-center gap-2 rounded-none border-b-2 border-transparent bg-transparent pb-3 text-sm text-muted-foreground shadow-none data-[state=active]:border-secondary data-[state=active]:text-secondary data-[state=active]:shadow-none -mb-px"
                              >
                                <MapPin className="w-4 h-4" />
                                ADDRESS
                              </TabsTrigger>
                              <TabsTrigger
                                value="parcel"
                                className="flex items-center justify-center gap-2 rounded-none border-b-2 border-transparent bg-transparent pb-3 text-sm text-muted-foreground shadow-none data-[state=active]:border-secondary data-[state=active]:text-secondary data-[state=active]:shadow-none -mb-px"
                              >
                                <Settings className="w-4 h-4" />
                                PARCEL/LAND
                              </TabsTrigger>
                            </TabsList>

                            <TabsContent
                              value="address"
                              className="mt-0 pt-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            >
                              {/* Full Address Display */}
                              <FindProperty
                                agreementCategory={agreementCategory}
                                onBtnClick={onBtnClick}
                                isAdminCreateUser={isAdminCreateUser}
                                isServiceProvider={isServiceProvider}
                                isByPassOptionAvailable={
                                  isByPassOptionAvailable
                                }
                                onChangeModalScreen={onChangeModalScreen}
                                onCloseModalHandler={onCloseModalHandler}
                                setShowPropertyListing={setShowPropertyListing}
                                propertyValues={propertyValues}
                                selectedData={editPropertiesDetails}
                                address={selectedData?.address}
                                editProperty={editProperty}
                                setPropertyValues={setPropertyValues}
                                validate={validate}
                                handleToggle={handleToggle}
                                handleClose={handleClose}
                                questionsErrMsg={questionsErrMsg}
                                open={open}
                                errMsg={errMsg}
                                setErrMsg={setErrMsg}
                                isResumeSignup={isResumeSignup}
                                selectedRowData={selectedRowData}
                                isAssistant={isAssistant}
                                backToCategoryWisePricingScreen={
                                  backToCategoryWisePricingScreen
                                }
                                isAdmin={isResumeSignup ? false : isAdmin}
                                isUpgradeCategory={isUpgradeCategory}
                                searchedAddress={searchedAddress}
                                setSearchedAddress={setSearchedAddress}
                                userEmail={userEmail}
                                hasSearched={hasSearched}
                                isTailwindComponent={isTailwindComponent}
                                isPropertyConfirmed={isPropertyConfirmed}
                              />
                            </TabsContent>

                            <TabsContent
                              value="parcel"
                              className="mt-0 overflow-visible pt-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            >
                              <FindPropertyByParcel
                                agreementCategory={agreementCategory}
                                category={resolvedCategory}
                                categoryChosenBeforeSearch={
                                  Boolean(isAdminCreateUser || isAdmin)
                                }
                                onBtnClick={onBtnClick}
                                isAdminCreateUser={isAdminCreateUser}
                                isServiceProvider={isServiceProvider}
                                isByPassOptionAvailable={
                                  isByPassOptionAvailable
                                }
                                onChangeModalScreen={onChangeModalScreen}
                                onCloseModalHandler={onCloseModalHandler}
                                setShowPropertyListing={setShowPropertyListing}
                                propertyValues={propertyValues}
                                setPropertyValues={setPropertyValues}
                                validate={validate}
                                handleToggle={handleToggle}
                                handleClose={handleClose}
                                questionsErrMsg={questionsErrMsg}
                                open={open}
                                errMsg={errMsg}
                                setErrMsg={setErrMsg}
                                editProperty={editProperty}
                                isAssistant={isAssistant}
                                isResumeSignup={isResumeSignup}
                                backToCategoryWisePricingScreen={
                                  backToCategoryWisePricingScreen
                                }
                                isAdmin={isResumeSignup ? false : isAdmin}
                                userEmail={userEmail}
                                isTailwindComponent={isTailwindComponent}
                                propertyQuestion={propertyQuestion}
                                hasSearched={hasSearched}
                                isPropertyConfirmed={isPropertyConfirmed}
                              />
                            </TabsContent>
                          </Tabs>
                        </div>

                        {/* Skip Button - shown when admin/resume user adds another property */}
                        {localStorage.getItem("button") === "skip" &&
                        typeof handleSkip === "function" ? (
                          <div className="flex justify-center mt-6 mb-4">
                            <Button
                              onClick={handleSkip}
                              variant="outline"
                              type="button"
                              className="px-8 py-2.5 rounded-full font-medium text-sm border-border hover:bg-muted/50 hover:text-black transition-all"
                            >
                              SKIP THIS STEP
                            </Button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Unit Number Modal */}
      <Dialog
        open={showUnitModal}
        onOpenChange={(open) => {
          if (unitModalActionsDisabled && !open) return;
          handleUnitModalClose(open);
        }}
      >
        <DialogContent
          className="sm:max-w-md p-0 gap-0 border-0 overflow-hidden"
          hideCloseButton={unitModalActionsDisabled}
          preventDismiss={unitModalActionsDisabled}
        >
          <DialogTitle className="sr-only">
            Multiple Properties Found
          </DialogTitle>
          <div className="p-8 text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary/20 to-cyan-400/20 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary/30 to-cyan-400/30 flex items-center justify-center">
                  <Building className="w-8 h-8 text-secondary" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-foreground mb-4">
              We have located multiple properties with the same address entered.
            </h2>

            <p className="text-muted-foreground mb-6">
              Is there a Unit or Condo Number?
            </p>

            {!showUnitInput ? (
              /* Yes/No Buttons */
              <div
                className={cn(
                  "flex gap-4 justify-center",
                  unitModalActionsDisabled && "pointer-events-none opacity-60",
                )}
              >
                <Button
                  onClick={
                    unitModalActionsDisabled ? undefined : handleUnitModalNo
                  }
                  disabled={propertyLoader || unitModalActionsDisabled}
                  variant="outline"
                  className="px-8 py-2.5 rounded-full font-medium"
                >
                  No
                </Button>
                <Button
                  disabled={propertyLoader || unitModalActionsDisabled}
                  onClick={
                    unitModalActionsDisabled ? undefined : handleUnitModalYes
                  }
                  className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-8 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
                >
                  Yes
                </Button>
              </div>
            ) : (
              /* Unit Input Form */
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "space-y-4",
                  unitModalActionsDisabled && "pointer-events-none opacity-60",
                )}
              >
                <div className="space-y-2">
                  <Label
                    htmlFor="modalUnit"
                    className="text-left block text-muted-foreground text-sm"
                  >
                    Unit Number
                  </Label>
                  <FormInput
                    name="modalUnit"
                    value={modalUnitNumber}
                    onChange={(e) => setModalUnitNumber(e.target.value)}
                    placeholder="Enter unit number"
                    className="h-10"
                    autoFocus
                    validation={{
                      values: { modalUnitNumber },
                      setFieldValue: (value) => setModalUnitNumber(value),
                      setFieldTouched: () => {},
                      touched: { modalUnitNumber: true },
                      errors: { modalUnitNumber: "" },
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-left">
                  Please include your unit or condominium number when submitting
                  your information so we can accurately locate and verify your
                  property.
                </p>
                {proeprtySearchErrorByUnit && (
                  <p className="text-sm text-red-500 text-center">
                    {proeprtySearchErrorByUnit || "Something went wrong."}{" "}
                    Please re enter your unit number and try again.
                  </p>
                )}
                <Button
                  onClick={
                    unitModalActionsDisabled ? undefined : handleUnitModalSubmit
                  }
                  disabled={!modalUnitNumber.trim() || unitModalActionsDisabled}
                  className="w-full bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
                >
                  Submit
                </Button>
              </motion.div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {complimentaryEnrollResult ? (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type={complimentaryEnrollResult.success ? "Success" : "Error"}
          openPopup={true}
          onClose={true}
          onCloseFun={() => {
            const succeeded = complimentaryEnrollResult.success;
            setComplimentaryEnrollResult(null);
            dispatch(userSigupAction.fetchPaymentData({}));
            if (succeeded) {
              onCloseModalHandler?.("success");
            }
          }}
          mesage={complimentaryEnrollResult.message}
          footer={false}
        />
      ) : null}

      <Loader
        open={
          Boolean(
            paymnetLoader && isAdminEldoradoComplimentaryEnhancedSelected(),
          ) || epAwareApplying
        }
      />
    </>
  );
};

export default FindPropertyComponent;
